import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateVesselEquipmentDetailsRequest,
    UpdateVesselEquipmentDetailsRequest,
    VesselEqType,
    VesselEquipmentDetail,
    VesselEquipmentDetailsRequest,
    VesselEquipmentDetailsResponse,
    VesselEquipmentTypesResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/vessel-equipment-details.api.ts';
import {
    createApiVesselEquipmentDetail,
    getApiVesselEquipmentDetails,
    getApiVesselEquipmentTypes,
    updateApiVesselEquipmentDetail,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/vessel-equipment-details.api.ts';
import { extractError } from '@utils/slice.ts';

export interface VesselEquipmentDetailsError {
    message?: string;
    [k: string]: any;
}

export interface VesselEquipmentDetailsState {
    error: VesselEquipmentDetailsError | null;

    commonVesselEquipmentDetails: VesselEquipmentDetailsResponse | null;
    vesselEquipmentDetails: VesselEquipmentDetail[];
    pagination: CommonTablePaginatedResponse;

    commonVesselEquipmentTypes: VesselEquipmentTypesResponse | null;
    vesselEquipmentTypes: VesselEqType[];
    typesPagination: CommonTablePaginatedResponse;

    updatedVesselEquipmentDetail: VesselEquipmentDetail | null;
    currentGetRequestId: string | null;

    isGetVesselEquipmentDetailsPending: boolean;
    isCreateVesselEquipmentDetailPending: boolean;
    isUpdateVesselEquipmentDetailPending: boolean;
    isGetVesselEquipmentTypesPending: boolean;
}

const emptyPagination: CommonTablePaginatedResponse = {
    pageNumber: 0,
    pageSize: 0,
    totalPages: 0,
    totalResults: 0,
    maxPageSize: 0,
};

const initialState: VesselEquipmentDetailsState = {
    error: null,

    commonVesselEquipmentDetails: null,
    vesselEquipmentDetails: [],
    pagination: emptyPagination,

    commonVesselEquipmentTypes: null,
    vesselEquipmentTypes: [],
    typesPagination: emptyPagination,

    updatedVesselEquipmentDetail: null,
    currentGetRequestId: null,

    isGetVesselEquipmentDetailsPending: false,
    isCreateVesselEquipmentDetailPending: false,
    isUpdateVesselEquipmentDetailPending: false,
    isGetVesselEquipmentTypesPending: false,
};

export const getVesselEquipmentDetails = createAsyncThunk<
    VesselEquipmentDetailsResponse,
    { clientId: number; tenantAlias: string; payload: VesselEquipmentDetailsRequest },
    { rejectValue: VesselEquipmentDetailsError }
>(
    'adminCenterVesselEquipmentDetails/getVesselEquipmentDetails',
    ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
        getApiVesselEquipmentDetails(clientId, tenantAlias, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const createVesselEquipmentDetail = createAsyncThunk<
    VesselEquipmentDetail,
    { clientId: number; tenantAlias: string; payload: CreateVesselEquipmentDetailsRequest },
    { rejectValue: VesselEquipmentDetailsError }
>(
    'adminCenterVesselEquipmentDetails/createVesselEquipmentDetail',
    ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
        createApiVesselEquipmentDetail(clientId, tenantAlias, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const updateVesselEquipmentDetail = createAsyncThunk<
    VesselEquipmentDetail,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateVesselEquipmentDetailsRequest },
    { rejectValue: VesselEquipmentDetailsError }
>(
    'adminCenterVesselEquipmentDetails/updateVesselEquipmentDetail',
    ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
        updateApiVesselEquipmentDetail(clientId, tenantAlias, id, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getVesselEquipmentTypes = createAsyncThunk<
    VesselEquipmentTypesResponse,
    { clientId: number; tenantAlias: string; payload: VesselEquipmentDetailsRequest },
    { rejectValue: VesselEquipmentDetailsError }
>(
    'adminCenterVesselEquipmentDetails/getVesselEquipmentTypes',
    ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
        getApiVesselEquipmentTypes(clientId, tenantAlias, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const vesselEquipmentDetailsSlice = createSlice({
    name: 'adminCenterVesselEquipmentDetails',
    initialState,
    reducers: {
        resetVesselEquipmentDetailsState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselEquipmentDetails.pending, (state, action) => {
            state.isGetVesselEquipmentDetailsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVesselEquipmentDetails.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselEquipmentDetailsPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonVesselEquipmentDetails = action.payload;
            state.vesselEquipmentDetails = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getVesselEquipmentDetails.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselEquipmentDetailsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createVesselEquipmentDetail.pending, (state) => {
            state.isCreateVesselEquipmentDetailPending = true;
            state.error = null;
        });
        builder.addCase(createVesselEquipmentDetail.fulfilled, (state, action) => {
            state.isCreateVesselEquipmentDetailPending = false;
            state.updatedVesselEquipmentDetail = action.payload;
        });
        builder.addCase(createVesselEquipmentDetail.rejected, (state, action) => {
            state.isCreateVesselEquipmentDetailPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselEquipmentDetail.pending, (state) => {
            state.isUpdateVesselEquipmentDetailPending = true;
            state.error = null;
        });
        builder.addCase(updateVesselEquipmentDetail.fulfilled, (state, action) => {
            state.isUpdateVesselEquipmentDetailPending = false;
            state.updatedVesselEquipmentDetail = action.payload;
        });
        builder.addCase(updateVesselEquipmentDetail.rejected, (state, action) => {
            state.isUpdateVesselEquipmentDetailPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getVesselEquipmentTypes.pending, (state) => {
            state.isGetVesselEquipmentTypesPending = true;
            state.error = null;
        });
        builder.addCase(getVesselEquipmentTypes.fulfilled, (state, action) => {
            state.isGetVesselEquipmentTypesPending = false;
            const { data, pagination } = action.payload;
            state.commonVesselEquipmentTypes = action.payload;
            state.vesselEquipmentTypes = data ?? [];
            state.typesPagination = pagination;
        });
        builder.addCase(getVesselEquipmentTypes.rejected, (state, action) => {
            state.isGetVesselEquipmentTypesPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { vesselEquipmentType, vesselEquipmentDetails } = action.payload;

            if (vesselEquipmentType) {
                const { data, pagination } = vesselEquipmentType;
                state.commonVesselEquipmentTypes = vesselEquipmentType;
                state.vesselEquipmentTypes = data ?? [];
                state.typesPagination = pagination;
            }

            if (vesselEquipmentDetails) {
                const { data, pagination } = vesselEquipmentDetails;
                state.commonVesselEquipmentDetails = vesselEquipmentDetails;
                state.vesselEquipmentDetails = data ?? [];
                state.pagination = pagination;
            }
        });
    },
});

export const { resetVesselEquipmentDetailsState } = vesselEquipmentDetailsSlice.actions;
export default vesselEquipmentDetailsSlice.reducer;
